import { useCallback, useEffect, useRef, useState } from "react";
import { call } from "@/services/callables";
import { useToast } from "@/contexts/ToastContext";
import { toAppError, type AppError } from "@/services/errors";

/**
 * A read from the backend.
 *
 * Three states, and pages must render all three: loading, failed, and loaded.
 * The version of this that returns only the data leads to pages that render an
 * empty table when a call fails, which reads as "you have nothing" rather than
 * "we could not ask".
 */
export interface Loadable<T> {
  data: T | undefined;
  loading: boolean;
  failed: boolean;
  /**
   * Why it failed, for a page that answers differently to different refusals.
   * A lesson that does not exist and a lesson somebody else taught are not the
   * same sentence, and only this tells them apart.
   */
  error: AppError | undefined;
  reload: () => void;
}

export function useCallable<TResponse, TRequest = undefined>(
  name: string,
  request?: TRequest,
  /** Skip the call entirely — for a page that must wait for something first. */
  enabled = true
): Loadable<TResponse> {
  const { showError } = useToast();

  /*
    Held in a ref rather than watched.

    A reporting callback that changed identity when it reported would make this
    effect re-run on every failure, which is an infinite request loop rather
    than a retry. The provider keeps it stable; this makes that a property of
    one file instead of two.
  */
  const report = useRef(showError);
  report.current = showError;
  const [data, setData] = useState<TResponse | undefined>(undefined);
  const [loading, setLoading] = useState(enabled);
  const [failed, setFailed] = useState(false);
  const [error, setError] = useState<AppError | undefined>(undefined);
  const [nonce, setNonce] = useState(0);

  // Serialised, so an object literal at the call site does not re-fetch on
  // every render. The alternative is asking every caller to memoise, which
  // someone will eventually forget in a way that is invisible until it is a
  // request storm.
  const key = request === undefined ? "" : JSON.stringify(request);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setFailed(false);
    setError(undefined);

    call<TRequest | undefined, TResponse>(name, key === "" ? undefined : (JSON.parse(key) as TRequest))
      .then((response) => {
        if (cancelled) return;
        setData(response);
        setLoading(false);
      })
      .catch((caught) => {
        if (cancelled) return;
        setFailed(true);
        setError(toAppError(caught));
        setLoading(false);
        report.current(caught);
      });

    return () => {
      cancelled = true;
    };
  }, [name, key, enabled, nonce]);

  const reload = useCallback(() => setNonce((current) => current + 1), []);
  return { data, loading, failed, error, reload };
}
